<template>
  <div class="block-selector" :class="{ 'dropdown-open': isOpen }">
    <multiselect
      v-model="internalValue"
      :options="actionOptions"
      :searchable="true"
      :close-on-select="true"
      :show-labels="false"
      open-direction="bottom"
      :placeholder="placeholder"
      label="displayName"
      track-by="_id"
      :loading="isLoading"
      :taggable="allowCustom"
      tag-placeholder="Utiliser cette valeur personnalisée"
      @tag="onCustomTag"
      @select="onSelect"
      @open="onOpen"
      @close="onClose"
      class="block-multiselect"
    >
      <template #option="{ option }">
        <div class="peelo-option">
          <div class="peelo-option-icon" v-if="!option.$isDisabled">
            <svg v-if="option.isSpecial && option._id === 'EMPTY'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
            <svg v-else-if="option.isSpecial && option._id === 'NO_ACTION'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
            <svg v-else-if="option.isCustom" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
          </div>
          <div class="peelo-option-content">
            <span v-if="option.isSpecial" class="option-special">{{ option.displayName }}</span>
            <span v-else-if="option.$isDisabled" class="option-separator">{{ option.displayName }}</span>
            <span v-else class="option-block">{{ option.displayName }} <small style="opacity: 0.6; margin-left: 5px;">({{ option.action_id }})</small></span>
          </div>
        </div>
      </template>
      <template #singleLabel="{ option }">
        <div class="peelo-single-action-chip" v-if="!option.isSpecial">
          <div class="peelo-single-icon">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
          </div>
          <div class="peelo-single-text">
            <span class="action-title">{{ option.displayName }}</span>
            <span class="action-subtitle" v-if="option.action_id && option.action_id !== option.displayName">({{ option.action_id }})</span>
          </div>
        </div>
        <div class="peelo-single-label" v-else>
          <span>{{ option.displayName }}</span>
        </div>
      </template>
      <template #caret="{ toggle }">
        <div class="peelo-caret" @mousedown.prevent.stop="toggle">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </template>
      <template #noResult>
        <div class="peelo-empty-state">
          <div class="empty-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <div class="empty-text">
            <h4>Aucune action trouvée</h4>
            <p v-if="allowCustom">Appuyez sur <kbd>Entrée</kbd> pour utiliser cette valeur.</p>
            <p v-else>Essayez de modifier votre recherche.</p>
          </div>
        </div>
      </template>
      <template #noOptions>
        <div class="peelo-empty-state">
          <div class="empty-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="9" x2="15" y2="15"></line>
              <line x1="15" y1="9" x2="9" y2="15"></line>
            </svg>
          </div>
          <div class="empty-text">
            <h4>La liste est vide</h4>
            <p>Aucune action disponible à déclencher.</p>
          </div>
        </div>
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import axios from 'axios';
import { consoleError } from '@/utils/logger';
import { actionsApiBaseUrl } from '@/config';
import 'vue-multiselect/dist/vue-multiselect.css';

export default {
  name: 'ActionSelector',
  components: {
    Multiselect
  },
  props: {
    value: {
      type: [String, Object],
      default: ''
    },
    modelValue: {
      type: [String, Object],
      default: ''
    },
    idBot: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Sélectionner une action'
    },
    allowNoAction: {
      type: Boolean,
      default: false
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    allowCustom: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      isLoading: false,
      actions: [],
      internalValue: null
    };
  },
  computed: {
    actionOptions() {
      const options = [];
      
      if (this.allowEmpty) {
        options.push({
          _id: 'EMPTY',
          action_id: '',
          displayName: '-- Aucune / Continuer --',
          isSpecial: true
        });
      }

      if (this.allowNoAction) {
        options.push({
          _id: 'NO_ACTION',
          action_id: 'NO_ACTION',
          displayName: 'Pas d\'action',
          isSpecial: true
        });
      }
      
      if (options.length > 0) {
        options.push({
          _id: 'SEPARATOR',
          action_id: 'SEPARATOR',
          displayName: '──────────────────',
          $isDisabled: true
        });
      }
      
      if (this.actions && this.actions.length > 0) {
        options.push(...this.actions);
      }
      
      return options;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.syncInternalValue(newVal);
      }
    },
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.syncInternalValue(newVal);
      }
    },
    actions() {
      this.syncInternalValue(this.modelValue || this.value);
    }
  },
  async created() {
    await this.fetchActions();
  },
  methods: {
    syncInternalValue(val) {
      if (!val) {
        if (this.allowEmpty) {
          this.internalValue = this.actionOptions.find(o => o._id === 'EMPTY') || null;
        } else {
          this.internalValue = null;
        }
        return;
      }
      
      if (typeof val === 'object' && val._id) {
        this.internalValue = val;
        return;
      }

      if (typeof val === 'string') {
        if (val === 'NO_ACTION' && this.allowNoAction) {
          this.internalValue = this.actionOptions.find(o => o._id === 'NO_ACTION');
          return;
        }
        
        const found = this.actions.find(a => a.action_id === val || a._id === val);
        if (found) {
          this.internalValue = found;
        } else {
          this.internalValue = { _id: val, action_id: val, displayName: val };
        }
      }
    },
    onSelect(selectedOption) {
      if (!selectedOption) return;
      
      let emitVal = '';
      if (selectedOption._id === 'EMPTY') {
        emitVal = '';
      } else if (selectedOption._id === 'NO_ACTION') {
        emitVal = 'NO_ACTION';
      } else {
        emitVal = selectedOption.action_id || selectedOption._id;
      }
      
      this.$emit('input', emitVal);
      this.$emit('update:modelValue', emitVal);
      this.$emit('change', selectedOption);
    },
    onCustomTag(newTag) {
      if (!this.allowCustom) return;
      const tag = {
        _id: newTag,
        action_id: newTag,
        displayName: newTag,
        isCustom: true
      };
      this.internalValue = tag;
      this.$emit('input', newTag);
      this.$emit('update:modelValue', newTag);
      this.$emit('change', tag);
    },
    onOpen() {
      this.isOpen = true;
      this.$emit('open');
    },
    onClose() {
      this.isOpen = false;
      this.$emit('close');
    },
    async fetchActions() {
      if (!this.idBot || this.idBot === 'undefined') {
        this.actions = [];
        return;
      }
      
      this.isLoading = true;
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          this.actions = [];
          return;
        }
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const response = await axios.get(
          `${actionsApiBaseUrl}/api/actions/${this.idBot}`,
          config
        );
        
        if (response.data && response.data.success) {
          // L'élément action du bloc amorce TOUS les types (manual / cron / event) -> on liste tout
          this.actions = (response.data.actions || []).map(a => ({
             ...a,
             displayName: a.name || a._id,
             action_id: a.action_id || a._id,
             _id: a._id || a.action_id
          }));
          this.$emit('actions-loaded', this.actions);
        } else {
          this.actions = [];
        }
      } catch (e) {
        consoleError('Erreur lors de la récupération des actions:', e);
        this.actions = [];
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style>
/* Surcharge premium des styles de vue-multiselect pour le design Peelo */
.block-selector {
  position: relative;
  z-index: 10;
  width: 100%;
}

.block-selector.dropdown-open {
  z-index: 9999 !important;
}

.block-multiselect {
  min-height: 48px;
}

.block-multiselect .multiselect__tags {
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-radius: 12px;
  background: #f8fafc;
  padding: 12px 40px 0 16px;
  min-height: 48px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
}

.block-multiselect:hover .multiselect__tags {
  background: #ffffff;
  border-color: rgba(20, 48, 40, 0.3);
}

.block-multiselect.multiselect--active .multiselect__tags {
  border-color: #2c463f;
  background: #ffffff;
  box-shadow: 0 4px 15px rgba(44, 70, 63, 0.1), 0 0 0 3px rgba(44, 70, 63, 0.2);
}

.block-multiselect .multiselect__single {
  background: transparent;
  font-size: 14.5px;
  color: #143028;
  font-weight: 500;
  margin-bottom: 0;
  padding-left: 0;
  line-height: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: 2px;
}

.peelo-single-action-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(218, 255, 209, 0.5);
  border: 1px solid rgba(20, 48, 40, 0.1);
  padding: 4px 12px 4px 4px;
  border-radius: 20px;
  transform: translateY(-4px);
}

.peelo-single-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #143028;
  color: #DAFFD1;
  border-radius: 50%;
}

.peelo-single-text {
  display: flex;
  align-items: center;
  gap: 6px;
}

.peelo-single-text .action-title {
  font-weight: 600;
  color: #143028;
  font-size: 13.5px;
}

.peelo-single-text .action-subtitle {
  color: #64748b;
  font-size: 11.5px;
  opacity: 0.8;
}

.block-multiselect .multiselect__placeholder {
  color: #94a3b8;
  font-size: 14.5px;
  margin-bottom: 0;
  padding-top: 0;
  line-height: 22px;
}

.block-multiselect .multiselect__input {
  background: transparent;
  font-size: 14.5px;
  color: #143028;
  padding: 0;
  margin-bottom: 0;
}

.block-multiselect .multiselect__content-wrapper {
  border: 1px solid rgba(20, 48, 40, 0.1);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(20, 48, 40, 0.12);
  margin-top: 8px;
  z-index: 10000;
  padding: 8px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* Option Styles */
.peelo-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.peelo-option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.peelo-option-content {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.block-multiselect .multiselect__option {
  padding: 8px;
  min-height: 48px;
  font-size: 14px;
  color: #334155;
  border-radius: 10px;
  margin-bottom: 2px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.block-multiselect .multiselect__option:last-child {
  margin-bottom: 0;
}

.block-multiselect .multiselect__option--highlight {
  background: rgba(44, 70, 63, 0.1) !important;
  color: #143028;
}

.block-multiselect .multiselect__option--highlight .peelo-option-icon {
  background: #2c463f;
  color: #ffffff;
}

.block-multiselect .multiselect__option--selected {
  background: #2c463f !important;
  color: #ffffff !important;
  font-weight: 600;
}

.block-multiselect .multiselect__option--selected .peelo-option-icon {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.block-multiselect .multiselect__option--selected.multiselect__option--highlight {
  background: #143028 !important;
}

.block-multiselect .multiselect__option--selected.multiselect__option--highlight .peelo-option-icon {
  background: rgba(255, 255, 255, 0.3);
}

/* Labels & Separators */
.option-special {
  font-style: italic;
  color: #64748b;
  font-weight: 500;
}

.option-separator {
  color: #cbd5e1;
  text-align: center;
  display: block;
  font-size: 12px;
  letter-spacing: 2px;
}

.multiselect__option--disabled {
  background: transparent !important;
  cursor: default;
}

/* Caret Customization */
.peelo-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
}

.peelo-caret:hover {
  background: #f1f5f9;
  color: #143028;
}

.multiselect--active .peelo-caret {
  transform: translateY(-50%) rotate(180deg);
  color: #143028;
}

/* Empty States */
.peelo-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
  background: #fafafa;
  border-radius: 12px;
  margin: 8px;
  border: 1px dashed #e2e8f0;
}

.empty-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin-bottom: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.03);
  position: relative;
}

.empty-icon-wrapper::after {
  content: '';
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.2);
  animation: pulse-empty-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse-empty-ring {
  0% { transform: scale(0.8); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: scale(1.1); opacity: 0; }
}

.empty-text h4 {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.empty-text p {
  margin: 0;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.4;
}

.empty-text kbd {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  color: #475569;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.block-multiselect .multiselect__select {
  display: none; /* Hide default caret */
}
</style>
